import { forwardRef, memo, useMemo, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@mui/material/Button';
// local imports
import { injectParams } from '../helpers/path';
import CardTitle, { type CardTitleProps } from './CardTitle';
import TargetIcon from './TargetIcon';
import SkillTrendIcon from './SkillTrendIcon';
// SCSS imports
import { skillDialogTitle, targetIcon, detailsButton } from './SkillLevelDialogTitle.module.scss';

type SkillLevelDialogTitleProps = {
  readonly route?: string | null;
  readonly abbr?: string | null;
  readonly title?: string | null;
  readonly isTarget?: boolean | null;
  readonly isInDemand?: boolean | null;
  readonly reducedUI?: boolean | null;
  readonly disabled?: boolean | null;
  readonly plain?: boolean | null;
  readonly subheader?: ReactNode;
  readonly action?: ReactNode;
  readonly sx?: CardTitleProps['sx'];
}

const SkillLevelDialogTitle = forwardRef<HTMLElement, SkillLevelDialogTitleProps>(({
  route,
  abbr,
  title,
  isTarget = false,
  isInDemand = false,
  reducedUI = false,
  disabled = false,
  plain = false,
  subheader,
  action,
  sx
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const link = useMemo(() => route && abbr ? injectParams(route, { skill_id: abbr }) : '', [route, abbr]);
  return (
    <CardTitle
        ref={ref}
        sx={sx}
        wrap
        title={title || 'skill.default_skill_title'}
        subheader={subheader || (!reducedUI && (!isMobile || isTarget || isInDemand) ? (
          <>
            <TargetIcon
                active={isTarget}
                tooltip={isTarget && !disabled ? 'skill.target_this' : undefined}
                tooltipPlacement="top"
                className={targetIcon}
            />
            <SkillTrendIcon active={isInDemand}/>
          </>
        ) : undefined)}
        action={action || (!plain && (
          <Button
              color="primary"
              variant="outlined"
              component={RouterLink}
              to={link}
              disabled={disabled || !link}
              className={detailsButton}
          >
            <FormattedMessage id="skill.view_details"/>
          </Button>
        ))}
        compact={!plain}
        className={skillDialogTitle}
    />
  );
});

SkillLevelDialogTitle.displayName = 'SkillLevelDialogTitle';

export default memo(SkillLevelDialogTitle);
